import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Stat Card Component for MBF Dashboard
 * Displays a metric with icon and left colored vertical line
 * 
 * @param {string|number} value - Main value to display
 * @param {string} subtitle - Secondary text below value
 * @param {React.ReactNode} icon - Icon component
 * @param {string} iconBgColor - Background color for icon container
 * @param {string} accentColor - Border and line accent color
 * @param {string} growth - Optional growth percentage (e.g., "+18%")
 * @param {string} growthColor - Color for growth text
 * @param {string} suffix - Optional suffix text (e.g., "MoM")
 * @param {string} bgColor - Optional background color for the card
 */
const StatCard = ({
    value,
    subtitle,
    icon,
    iconBgColor = '#f0d2ff',
    accentColor = '#c144ff',
    growth,
    growthColor = '#22c55e',
    suffix,
    bgColor = '#ffffff',
}) => {
    return (
        <Box
            sx={{
                bgcolor: bgColor,
                borderRadius: '12px',
                border: `1px solid ${accentColor}`,
                padding: '12px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: '108px',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                transition: 'box-shadow 0.2s ease',
                '&:hover': {
                    boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.1)',
                },
            }}
        >
            {/* Left side - Line + Content */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '12px',
                    alignItems: 'center',
                    flex: 1,
                }}
            >
                {/* Vertical Accent Line */}
                <Box
                    sx={{
                        width: '4px',
                        height: '40px',
                        bgcolor: accentColor,
                        borderRadius: '2px',
                        flexShrink: 0,
                    }}
                />

                {/* Metric Details */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                    }}
                >
                    {/* Value Row */}
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                        <Typography
                            sx={{
                                color: '#333333',
                                fontFamily: '"Roboto", sans-serif',
                                fontSize: '32px',
                                lineHeight: '40px',
                                fontWeight: 700,
                            }}
                        >
                            {value}
                        </Typography>
                        {growth && (
                            <Typography
                                sx={{
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    color: growthColor,
                                }}
                            >
                                {growth}
                            </Typography>
                        )}
                        {suffix && (
                            <Typography
                                sx={{
                                    fontSize: '12px',
                                    fontWeight: 400,
                                    color: '#8c8c8c',
                                }}
                            >
                                {suffix}
                            </Typography>
                        )}
                    </Box>

                    {/* Description */}
                    <Typography
                        sx={{
                            color: '#4d4d4d',
                            fontFamily: '"Roboto", sans-serif',
                            fontSize: '14px',
                            lineHeight: '20px',
                            fontWeight: 500,
                        }}
                    >
                        {subtitle}
                    </Typography>
                </Box>
            </Box>

            {/* Right side - Icon Container */}
            <Box
                sx={{
                    bgcolor: iconBgColor,
                    borderRadius: '12px',
                    padding: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                {icon}
            </Box>
        </Box>
    );
};

export default StatCard;
